import { DoubleRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { DatePicker, InputNumber, message, Modal, ModalProps, Tooltip, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import moment, { Moment } from 'moment';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import usePrefetchPages from '../hooks/usePrefetchPages';
import { CandidateDetail, JobPostingDetail } from '../types';
import Form from './Form';
import SankhyaAdmissionForm from './SankhyaAdmissionForm';

interface SendSankhyaAdmissionFormValues {
  admissionDate: Moment;
  salary: number;
  candidateEmail: string;
  candidateCPF: string;
  sankhyaJobRole: string;
  sankhyaCompany: string;
  sankhyaDepartment: string;
}

interface SendSankhyaAdmissionModalProps extends ModalProps {
  jobPostingId: JobPostingDetail['id'];
  candidateId?: CandidateDetail['id'];
  onSuccess?: (
    data: AxiosResponse<unknown, unknown> | void,
    variables: SendSankhyaAdmissionFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const SendSankhyaAdmissionModal = ({
  jobPostingId,
  candidateId,
  onSuccess,
  ...props
}: SendSankhyaAdmissionModalProps) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const candidate = useAPIQuery([`/candidates/${candidateId}/`], { enabled: !!candidateId });

  const jobPosting = useAPIQuery([`/job-postings/${jobPostingId}/`]);

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: candidateId,
          job_posting: jobPostingId,
        },
      },
    ],
    {
      enabled: !!candidateId,
      select(data) {
        return data.results[0];
      },
    }
  );

  const sankhyaJobPosting = useAPIQuery(
    [
      '/sankhya/sankhya-job-postings/',
      {
        params: {
          job_posting_id: jobPostingId,
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      select(data) {
        return data.count > 0 ? data.results[0] : undefined;
      },
    }
  );

  const sankhyaCompaniesPages = useInfiniteAPIQuery(
    [
      '/sankhya/companies/',
      {
        params: {
          offsetPage: '0',
          fieldsetList: 'CODEMP',
          joinFieldsetList: 'NOMEFANTASIA',
          sort: 'CODEMP',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaCompanies = usePrefetchPages(sankhyaCompaniesPages, (data) => data.results);

  const sankhyaJobRolesPages = useInfiniteAPIQuery(
    [
      '/sankhya/job-roles/',
      {
        params: {
          fieldsetList: 'CODCARGO,DESCRCARGO',
          filter: {
            ATIVO: 'S',
          },
          sort: 'CODCARGO',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaJobRoles = usePrefetchPages(sankhyaJobRolesPages, (data) => data.results);

  const sankhyaDepartmentsPages = useInfiniteAPIQuery(
    [
      '/sankhya/departments/',
      {
        params: {
          fieldsetList: 'CODDEP,DESCRDEP',
          filter: {
            ATIVO: 'S',
            ANALITICO: 'S',
          },
          sort: 'CODDEP',
        },
      },
    ],
    {
      enabled: !!config.data?.enableSankhyaAdmission,
      pageKey: 'offsetPage',
    }
  );
  const sankhyaDepartments = usePrefetchPages(sankhyaDepartmentsPages, (data) => data.results);

  const startSalaryRange = jobPosting.data?.start_salary_range;
  const endSalaryRange = jobPosting.data?.end_salary_range;

  const mutation = useAPIMutation(
    async (
      { salary, sankhyaJobRole, sankhyaCompany, sankhyaDepartment }: SendSankhyaAdmissionFormValues,
      api
    ) => {
      const recruitmentProcesses = await api.get('/recruitment-processes/', {
        params: {
          job_posting: jobPostingId,
          candidate: candidateId,
          status: 'ACTIVE',
        },
      });

      const recruitmentProcess =
        recruitmentProcesses.data.count > 0 ? recruitmentProcesses.data.results[0] : null;

      if (recruitmentProcess) {
        const sankhyaJobPostings = await api.get('/sankhya/sankhya-job-postings/', {
          params: {
            job_posting_id: jobPostingId,
          },
        });

        const sankhyaJobPosting =
          sankhyaJobPostings.data.count > 0 ? sankhyaJobPostings.data.results[0] : null;

        // If SankhyaJobPosting doesn't exist, create it
        if (!sankhyaJobPosting) {
          await api.post(`/sankhya/sankhya-job-postings/`, {
            job_posting: jobPostingId,
            company: sankhyaCompany,
            job_role: sankhyaJobRole,
            department: sankhyaDepartment,
          });
        }
        // If SankhyaJobPosting exists but doesn't have all the informations, update it
        else if (
          !(sankhyaJobPosting.company && sankhyaJobPosting.job_role && sankhyaJobPosting.department)
        ) {
          await api.patch(`/sankhya/sankhya-job-postings/${sankhyaJobPosting.id}/`, {
            company: sankhyaCompany,
            job_role: sankhyaJobRole,
            department: sankhyaDepartment,
          });
        }

        await api.patch(`/recruitment-processes/${recruitmentProcess.id}/`, {
          salary,
        });

        await api.post('/sankhya/sankhya-admissions/', {
          recruitment_process: recruitmentProcess.id,
        });
      }
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/sankhya/sankhya-admissions/');
      },
      onSuccess: async (data, variables, context) => {
        message.success('Admissão enviada.');

        onSuccess && onSuccess(data, variables, context);
      },
    }
  );

  return (
    <Modal
      okButtonProps={{
        htmlType: 'submit',
        icon: <DoubleRightOutlined />,
        loading: mutation.isLoading,
      }}
      okText="Enviar"
      onOk={async () => form.submit()}
      title={
        <Typography.Text>
          Enviar admissão: <Typography.Text strong>{candidate.data?.name}</Typography.Text>
        </Typography.Text>
      }
      {...props}
    >
      <Helmet>
        <title>{`Enviar admissão ${candidate.data?.name}`} | Recrutamento e Seleção</title>
      </Helmet>
      <Form<SendSankhyaAdmissionFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          admissionDate: moment(recruitmentProcess.data?.admission_date),
          salary: recruitmentProcess.data?.salary,
          candidateEmail: candidate.data?.email || '',
          candidateCPF: candidate.data?.cpf || '',
          sankhyaCompany: sankhyaJobPosting.data?.company,
          sankhyaJobRole: sankhyaJobPosting.data?.job_role,
          sankhyaDepartment: sankhyaJobPosting.data?.department,
        }}
        onFinish={async (values) => {
          mutation.mutate(values);
        }}
      >
        <Form.Row>
          <Form.Col span={12}>
            <Form.Item
              label="Data de admissão"
              name="admissionDate"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <DatePicker
                disabledDate={(current) => {
                  // Days before today are not allowed
                  return current && current < moment().startOf('day');
                }}
                style={{ width: '100%' }}
                disabled={!!recruitmentProcess.data?.admission_date}
              />
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Item
              label={
                <Typography.Text>
                  Salário{' '}
                  <Tooltip
                    title={
                      endSalaryRange
                        ? startSalaryRange
                          ? `Faixa salarial definida entre R$ ${startSalaryRange
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} e R$ ${endSalaryRange
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
                          : `Salário máximo definido de R$ ${endSalaryRange
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
                        : 'Vaga sem informação de Faixa Salarial'
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Typography.Text>
              }
              name="salary"
              rules={[
                {
                  required: !!endSalaryRange,
                },
                {
                  validator(rule, value) {
                    if (value && endSalaryRange) {
                      if (
                        startSalaryRange &&
                        (value > endSalaryRange || value < startSalaryRange)
                      ) {
                        return Promise.reject(
                          new Error(
                            `O salário deve estar entre R$ ${startSalaryRange
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} e R$ ${endSalaryRange
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
                          )
                        );
                      }
                      if (value > endSalaryRange) {
                        return Promise.reject(
                          new Error(
                            `O salário deve ser menor que R$ ${endSalaryRange
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
                          )
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<string | number>
                prefix="R$"
                style={{ width: '100%' }}
                step={100}
                min={0}
                max={2147483647}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                parser={(displayValue) => {
                  if (!displayValue) {
                    return '';
                  }
                  return parseInt(displayValue?.replace(/\$\s?|(\.*)/g, '') || '0', 10);
                }}
                disabled={!!recruitmentProcess.data?.salary}
              />
            </Form.Item>
          </Form.Col>
        </Form.Row>
        <SankhyaAdmissionForm
          sankhyaJobPosting={sankhyaJobPosting.data}
          sankhyaCompanies={sankhyaCompanies}
          sankhyaJobRoles={sankhyaJobRoles}
          sankhyaDepartments={sankhyaDepartments}
        />
      </Form>
    </Modal>
  );
};

export default SendSankhyaAdmissionModal;
